import { Button, Card, TextField, useToast } from '@citizen-walk/shared-ui';
import {
  AI_DAILY_QUOTA_PER_DISTRICT,
  AI_DAILY_QUOTA_PER_DISTRICT_MAX,
  AI_DAILY_QUOTA_PER_USER,
  AI_DAILY_QUOTA_PER_USER_MAX,
  parseAiDailyQuotaInput,
  resolveAiDailyQuotaLimits,
  type District,
} from '@citizen-walk/shared-types';
import { useState } from 'react';
import { deleteField, doc, serverTimestamp, setDoc, type FieldValue } from 'firebase/firestore';
import { db } from '../../lib/firebase';

/**
 * AI 解析の 1 日の上限 panel (docs/13 §9-3、docs/98 R-101 の 3)。
 *
 * 地区 doc の `aiAnalysis.dailyQuota.{perDistrict, perUser}` を編集する。空欄 = 既定値
 * (地区 3000 / 参加者 150 件/日、shared-types `aiDailyQuota.ts` が正本) を使う = 保存値を消す。
 *
 * master のみ編集可 (`aiAnalysis` は firestore.rules で master 専管)。地区管理者には現在の値を
 * 読み取り専用で見せる (同じ画面の他の panel と同じ `canEdit` 方式)。
 *
 * 今日の使用量 (`_rateLimits` の地区カウンタ) は rules で client から読めない (read/write とも deny)
 * ため表示しない。
 *
 * 保存は他の panel と同じく `setDoc(..., { merge: true })`。merge は入れ子の map を再帰マージするため
 * `aiAnalysis` の他のキー (enabled / settingsId / 各種上書き) は温存される。
 */

export interface AiDailyQuotaInputState {
  perDistrict: string;
  perUser: string;
}

/** pure: 地区 doc の保存値を入力欄の文字列にする (未設定・不正値は空欄 = 既定値)。 */
export function readAiDailyQuotaInput(
  district: Pick<District, 'aiAnalysis'>,
): AiDailyQuotaInputState {
  const q = (district.aiAnalysis as { dailyQuota?: unknown } | undefined)?.dailyQuota as
    | { perDistrict?: unknown; perUser?: unknown }
    | null
    | undefined;
  const toStr = (v: unknown) =>
    typeof v === 'number' && Number.isFinite(v) && v >= 1 ? String(Math.floor(v)) : '';
  return { perDistrict: toStr(q?.perDistrict), perUser: toStr(q?.perUser) };
}

/**
 * pure: 検証済みの値を Firestore へ書く `aiAnalysis.dailyQuota` の値にする。
 * 空欄 (null) の項目は消す。両方空欄なら dailyQuota ごと消す。
 */
export function buildAiDailyQuotaWrite(
  perDistrict: number | null,
  perUser: number | null,
  del: () => FieldValue = deleteField,
): FieldValue | { perDistrict: number | FieldValue; perUser: number | FieldValue } {
  if (perDistrict === null && perUser === null) return del();
  return {
    perDistrict: perDistrict ?? del(),
    perUser: perUser ?? del(),
  };
}

export interface DistrictAiDailyQuotaPanelProps {
  district: District;
  uid: string;
  canEdit: boolean;
}

export function DistrictAiDailyQuotaPanel({
  district,
  uid,
  canEdit,
}: DistrictAiDailyQuotaPanelProps) {
  const { toast } = useToast();
  const [input, setInput] = useState<AiDailyQuotaInputState>(() => readAiDailyQuotaInput(district));
  const [errors, setErrors] = useState<{ perDistrict?: string; perUser?: string }>({});
  const [saving, setSaving] = useState(false);

  const effective = resolveAiDailyQuotaLimits(district);

  function update(key: keyof AiDailyQuotaInputState, value: string) {
    setInput((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  async function handleSave() {
    if (!canEdit) return;
    const parsed = parseAiDailyQuotaInput(input);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      toast('error', '入力内容を確認してください');
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      await setDoc(
        doc(db, 'districts', district.id),
        {
          aiAnalysis: { dailyQuota: buildAiDailyQuotaWrite(parsed.perDistrict, parsed.perUser) },
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      setInput({
        perDistrict: parsed.perDistrict === null ? '' : String(parsed.perDistrict),
        perUser: parsed.perUser === null ? '' : String(parsed.perUser),
      });
      toast('success', 'AI 解析の 1 日の上限を保存しました');
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card padding="lg" className="border-primary-200" data-testid="district-ai-daily-quota-panel">
      <h3 className="text-base font-bold text-slate-900 mb-3">📊 AI 解析の 1 日の上限</h3>
      <p className="text-xs text-slate-500 mb-1">
        1 日に AI
        解析できる件数の上限です。超えた投稿は解析を見送り、管理者があとから再解析できます。
        日付は日本時間の 0 時で切り替わります。
      </p>
      <p className="text-xs text-slate-500 mb-3">
        投稿の解析は観点 1 つにつき 1 件、AI との対話は 1 回につき 1 件と数えます。
        空欄のときは既定値 (地区あたり {AI_DAILY_QUOTA_PER_DISTRICT.toLocaleString('ja-JP')}{' '}
        件、参加者 1 人あたり {AI_DAILY_QUOTA_PER_USER.toLocaleString('ja-JP')} 件) を使います。
      </p>
      {!canEdit && (
        <p className="text-xs text-slate-600 mb-3" data-testid="ai-daily-quota-readonly-note">
          この設定はマスター管理者だけが変更できます。
        </p>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 mb-3">
        <TextField
          label="地区あたりの上限 (件/日)"
          value={input.perDistrict}
          onChange={(v) => update('perDistrict', v)}
          placeholder={`既定 ${AI_DAILY_QUOTA_PER_DISTRICT}`}
          disabled={!canEdit || saving}
          error={errors.perDistrict}
          hint={`1〜${AI_DAILY_QUOTA_PER_DISTRICT_MAX.toLocaleString('ja-JP')}。地区全体で 1 日に解析できる件数です。`}
          className="min-w-0"
        />
        <TextField
          label="参加者 1 人あたりの上限 (件/日)"
          value={input.perUser}
          onChange={(v) => update('perUser', v)}
          placeholder={`既定 ${AI_DAILY_QUOTA_PER_USER}`}
          disabled={!canEdit || saving}
          error={errors.perUser}
          hint={`1〜${AI_DAILY_QUOTA_PER_USER_MAX.toLocaleString('ja-JP')}。1 人の参加者が 1 日に解析できる件数です (複数の地区に参加している場合は合計で数えます)。`}
          className="min-w-0"
        />
      </div>

      <p className="text-xs text-slate-700 mb-3" data-testid="ai-daily-quota-current">
        現在の上限: 地区あたり <strong>{effective.perDistrict.toLocaleString('ja-JP')}</strong>{' '}
        件/日、 参加者 1 人あたり <strong>{effective.perUser.toLocaleString('ja-JP')}</strong> 件/日
      </p>

      <div className="flex justify-end">
        <Button
          onClick={() => void handleSave()}
          disabled={!canEdit || saving}
          data-testid="ai-daily-quota-save"
        >
          {saving ? '保存中…' : '保存'}
        </Button>
      </div>
    </Card>
  );
}
